import type { QueriesObject, System } from '../../odie';
import { GameScene } from '../GameScene';
import { BlockComponent } from '../components/BlockComponent';
import { BlockEntity, despawnBlockEntity } from '../entities/BlockEntity';

export type DespawnSource = 'attack' | 'mergeEffect';
interface DespawnInterceptor {
  interceptDespawn(
    entity: BlockEntity,
    source?: DespawnSource,
  ): Promise<boolean>;
  interceptDespawnPriority?: number;
}
/**
 * Experimental system to intercept block despawns. Interceptor can be used to animate the despawn on
 * its own, instead of using the default despawn animation.
 * Currently only the BasicCollision and MergeEffect are despawning blocks through this system.
 */
export class DespawnSystem implements System {
  public static readonly NAME = 'despawnSystem';
  public static Queries: QueriesObject = {
    block: {
      components: [BlockComponent],
      modified: true,
    },
  };
  public scene!: GameScene;

  /** List of interceptors that will be called when a block is going to be despawned */
  private interceptors: DespawnInterceptor[] = [];

  public modifiedQuery(
    entity: BlockEntity,
    block: BlockComponent,
    properties: { despawnSource: DespawnSource },
  ) {
    // If the block is already despawning, do not despawn it again
    if (properties.despawnSource && !block.asyncDespawnPromise) {
      // Despawn the block, storing the promise in the block component, so that can be awaited by the caller
      block.asyncDespawnPromise = this.asyncDespawn(
        entity,
        properties.despawnSource,
      );
    }
  }

  /**
   * Add an interceptor that will be called when a block is going to be despawned.
   */
  public addDespawnInterceptor(handler: DespawnInterceptor, priority?: number) {
    // Do not add the same interceptor twice
    if (this.interceptors.includes(handler)) return;

    // Set the priority of the interceptor, if provided
    if (priority !== undefined) {
      handler.interceptDespawnPriority = priority;
    }

    // Add the interceptor to the list
    this.interceptors.push(handler);

    // Sort interceptors by priority - the higher the priority, the earlier the interceptor will be called
    this.interceptors.sort((a, b) => {
      const pa = a.interceptDespawnPriority ?? 0;
      const pb = b.interceptDespawnPriority ?? 0;
      return pb - pa;
    });
  }

  /**
   * Async despawn a block, calling the interceptors before actually despawning it.
   */
  private async asyncDespawn(entity: BlockEntity, source: DespawnSource) {
    for (const handler of this.interceptors) {
      const intercepted = await handler.interceptDespawn(entity, source);
      // Block despawn should be intercepted only once, so break the loop if that happens
      if (intercepted) break;
    }
    // Actually despawn the block
    despawnBlockEntity(this.scene, entity);
  }
}
